import { NavLink, useLocation } from "react-router-dom";
import { MENU } from "../constants";
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
} from "@heroicons/react/outline";
import { LogoutIcon } from "@heroicons/react/outline";
import { useAuthStore } from "../store/auth.store";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

// const SidebarItem = ({ item, depth = 0, collapsed, openMenus, toggleMenu }) => {
//   const location = useLocation();
//   const hasChildren = item.children && item.children.length > 0;

//   const isActive = item.path
//     ? location.pathname.startsWith(item.path)
//     : item.children?.some((child) =>
//         location.pathname.startsWith(child.path || ""),
//       );

//   const isOpen = openMenus[item.label] ?? isActive;

//   const paddingLeft = 12 + depth * 16;

//   if (hasChildren) {
//     return (
//       <div>
//         <button
//           onClick={() => toggleMenu(item.label)}
//           className={`flex items-center justify-between w-full py-2 rounded-md
//             ${isActive ? "bg-pink-600 text-white" : "text-slate-300 hover:bg-slate-800"}
//           `}
//           style={{ paddingLeft }}
//         >
//           <span className="text-xs uppercase tracking-wider">{item.label}</span>

//           {!collapsed && (
//             <ChevronDownIcon
//               className={`w-4 h-4 transition-transform ${
//                 isOpen ? "rotate-180" : ""
//               }`}
//             />
//           )}
//         </button>

//         {!collapsed && isOpen && (
//           <div className="mt-1 space-y-1">
//             {item.children.map((child) => (
//               <SidebarItem
//                 key={child.label}
//                 item={child}
//                 depth={depth + 1}
//                 collapsed={collapsed}
//                 openMenus={openMenus}
//                 toggleMenu={toggleMenu}
//               />
//             ))}
//           </div>
//         )}
//       </div>
//     );
//   }

//   return (
//     <NavLink
//       to={item.path}
//       className={({ isActive }) =>
//         `block py-2 rounded-md text-xs uppercase transition
//         ${
//           isActive
//             ? "bg-slate-800 text-white"
//             : "text-slate-300 hover:bg-slate-800 hover:text-white"
//         }`
//       }
//       style={{ paddingLeft }}
//     >
//       {item.label}
//     </NavLink>
//   );
// };

const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const logout = useAuthStore((state) => state.logout);
  const hasPermission = useAuthStore((state) => state.hasPermission);

  const [openMenus, setOpenMenus] = useState({});

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const filterMenu = (menus) => {
    return menus
      .map((menu) => {
        if (menu.children) {
          const filteredChildren = filterMenu(menu.children);

          if (filteredChildren.length > 0) {
            return { ...menu, children: filteredChildren };
          }

          // fallback to parent permission
          if (!menu.permission || hasPermission(menu.permission)) {
            return { ...menu, children: [] };
          }

          return null;
        }

        // leaf node
        if (!menu.permission || hasPermission(menu.permission)) {
          return menu;
        }

        return null;
      })
      .filter(Boolean);
  };

  const visibleMenus = filterMenu(MENU);

  
  return (
    <aside
      className={`fixed left-0 top-0 h-screen overflow-hidden bg-slate-900 border-r border-white/10
      transition-all duration-300
      ${collapsed ? "w-16" : "w-64"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 h-16">
        {!collapsed && (
          <span className="text-xs text-slate-400 uppercase tracking-[0.16em]">
            Afrodise Dashboard
          </span>
        )}

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-300 hover:text-white"
        >
          {collapsed ? (
            <ChevronRightIcon className="w-6 h-6" />
          ) : (
            <ChevronLeftIcon className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Menu */}
      <nav className="px-2 space-y-3 mt-4">
        {visibleMenus.map((menu) => {
          const MenuIcon = menu.icon;

          if (menu.permission && !hasPermission(menu.permission)) {
            return null;
          }

          // Menu with children
          if (menu.children) {
            const isParentActive = menu.children.some((child) =>
              location.pathname.startsWith(child.path),
            );

            const isOpen = openMenus[menu.label] ?? isParentActive;

            return (
              <div key={menu.label}>
                {/* Parent */}
                <button
                  onClick={() => toggleMenu(menu.label)}
                  className={`flex items-center justify-between w-full px-3 py-3 rounded-md
                  ${
                    isParentActive
                      ? "bg-pink-600 text-white"
                      : "text-slate-300 hover:bg-slate-800"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <MenuIcon className="w-5 h-5 shrink-0" />

                    {!collapsed && (
                      <span className="text-xs uppercase tracking-wider">
                        {menu.label}
                      </span>
                    )}
                  </div>

                  {!collapsed && (
                    <ChevronDownIcon
                      className={`w-4 h-4 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>

                {/* Children */}
                {!collapsed && isOpen && (
                  <div className="mt-1 space-y-1">
                    {menu.children.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) =>
                          `block px-8 py-2 rounded-md text-xs uppercase transition
                          ${
                            isActive
                              ? "bg-slate-800 text-white"
                              : "text-slate-300 hover:bg-slate-800 hover:text-white"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Menu without children (direct link)
          return (
            <NavLink
              key={menu.label}
              to={menu.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md transition
                ${
                  isActive
                    ? "bg-pink-600 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <MenuIcon className="w-5 h-5 shrink-0" />
              {!collapsed && (
                <span className="text-xs uppercase tracking-wider">
                  {menu.label}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {visibleMenus.length === 0 && (
        <div className="px-4 mt-6 text-xs text-slate-400">
          No accessible sections
        </div>
      )}

      {/* Logout */}
      <div className="absolute bottom-0 w-full p-2 border-t border-white/10">
        {!collapsed && (
          <div className="px-4 text-[8px] text-slate-500">
            Version 1.0 | Build 57
          </div>
        )}

        <button
          onClick={() => {
            logout();
            navigate("/", { replace: true });
          }}
          className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-300 rounded-md hover:bg-red-500/10 hover:text-red-400 transition"
        >
          <LogoutIcon className="w-5 h-5 shrink-0" />

          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
